import { useLocalStorage } from "PosterEditor/hooks/use-locale-storage";
import { useState, useEffect } from "react";
import { TemplateCollection } from "PosterEditor/model/template-collection";
import { v4 as uuid } from "uuid";
import { defaultTemplateCollection } from "PosterEditor/model/default-project-style";

export const useCollectionLocalStore = () => {
  const { save, read, remove } = useLocalStorage();
  const [collections, setCollections] = useState<TemplateCollection[]>([]);

  useEffect(() => {
    loadCollections();
  }, []);

  const createCollection = () => {
    const collection = { ...defaultTemplateCollection, id: uuid() };
    saveCollection(collection);
    return collection;
  };

  const deleteCollection = (collection: TemplateCollection) => {
    setCollections((prev) => {
      const newCollections = prev ? [...prev] : [];
      const index = newCollections.findIndex((c) => c.id === collection.id);

      let updatedCollections;
      if (index !== -1) {
        updatedCollections = newCollections.filter(
          (c) => c.id !== collection.id
        );
      } else {
        updatedCollections = [...newCollections];
      }

      save("collectionStore", updatedCollections);
      return [...updatedCollections];
    });
  };

  const deleteAllCollections = () => {
    setCollections([]);
    remove("collectionStore");
  };

  const saveCollection = (collection: TemplateCollection) => {
    const collections = loadCollections();
    if (!collections) {
      setCollections([collection]);
      save("collectionStore", [collection]);

      return;
    }

    const newCollections = [...collections];
    const index = collections.findIndex((c) => c.id === collection.id);
    let updatedCollections;
    if (index !== -1) {
      updatedCollections = newCollections.map((c) =>
        c.id === collection.id ? collection : c
      );
    } else {
      updatedCollections = [{ ...collection }, ...newCollections];
    }

    const sortedCollections = updatedCollections.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setCollections(sortedCollections);
    save("collectionStore", sortedCollections);
    return sortedCollections;
  };

  const loadCollections = () => {
    const collections = read("collectionStore") as
      | TemplateCollection[]
      | undefined;

    if (!collections) {
      setCollections([]);
      return;
    }

    const sortedCollections = collections.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setCollections(sortedCollections);
    return sortedCollections;
  };

  const loadCollection = (id: string) => {
    const collections = read("collectionStore") as
      | TemplateCollection[]
      | undefined;
    return collections?.filter((c) => c.id === id)[0];
  };

  return {
    loadCollections,
    loadCollection,
    saveCollection,
    createCollection,
    deleteCollection,
    deleteAllCollections,
    collections,
  };
};
